import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, BookOpen, Trophy } from 'lucide-react';
type PackId = 'esencial' | 'completa';
const PACKS: Record<PackId, {
  id: PackId;
  icon: React.ReactNode;
  name: string;
  description: string;
  oldPrice: string;
  price: string;
  currency: string;
  badge?: string;
  saves?: string;
  ctaLabel: string;
  url: string;
}> = {
  esencial: {
    id: 'esencial',
    icon: <BookOpen className="w-7 h-7 text-gold-400" data-id="element-238" />,
    name: 'GUÍA ESENCIAL',
    description: '6 tomos + 6 bonos',
    oldPrice: '$89.000',
    price: '$19.990',
    currency: 'ARS',
    ctaLabel: '→ QUIERO LA GUÍA ESENCIAL',
    url: 'https://www.mundorecursos.store/cart/45187255959631:1'
  },
  completa: {
    id: 'completa',
    icon: <Trophy className="w-7 h-7 text-gold-400" data-id="element-239" />,
    name: 'GUÍA COMPLETA',
    description: '6 tomos + 6 bonos + El Acelerador de Calma™ (implementación diaria, lectura de señales y auditoría semanal)',
    oldPrice: '$119.000',
    price: '$27.990',
    currency: 'ARS',
    badge: 'MÁS ELEGIDO',
    saves: 'AHORRÁS $8.000',
    ctaLabel: '→ SÍ, QUIERO LA GUÍA COMPLETA',
    url: 'https://www.mundorecursos.store/cart/45187260416079:1'
  }
};
interface PackCardProps {
  pack: (typeof PACKS)[PackId];
  selected: boolean;
  onSelect: () => void;
}
function PackCard({
  pack,
  selected,
  onSelect
}: PackCardProps) {
  const isCompleta = pack.id === 'completa';
  return <button type="button" onClick={onSelect} aria-pressed={selected} className={`group w-full text-left rounded-2xl p-5 sm:p-6 border-2 transition-all duration-300 relative ${selected ? 'border-gold-500 bg-navy-800/80 shadow-[0_0_30px_rgba(212,168,74,0.18)]' : 'border-navy-700 bg-navy-900/60 hover:border-gold-500/40 hover:bg-navy-800/60'}`} data-id="element-240">
      <div className="flex items-start gap-4" data-id="element-241">
        {/* Radio */}
        <div className="pt-1 shrink-0" data-id="element-242">
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selected ? 'border-gold-400' : 'border-navy-600 group-hover:border-gold-500/50'}`} data-id="element-243">
            {selected && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 20
          }} className="w-3 h-3 rounded-full bg-gold-400" data-id="element-244" />}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0" data-id="element-245">
          <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap" data-id="element-246">
            {/* Left: title + meta */}
            <div className="flex-1 min-w-0" data-id="element-247">
              <div className="mb-2" data-id="element-248">{pack.icon}</div>
              <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight" data-id="element-249">
                {pack.name}
              </h3>

              {/* Badges (Completa only) */}
              {isCompleta && <div className="flex flex-wrap gap-2 mt-3" data-id="element-250">
                  {pack.badge && <span className="inline-flex items-center gap-1.5 bg-gold-500 text-navy-900 font-bold px-3 py-1 rounded-full text-xs" data-id="element-251">
                      <Star className="w-3.5 h-3.5 fill-navy-900" data-id="element-252" />{' '}
                      {pack.badge}
                    </span>}
                  {pack.saves && <span className="inline-flex items-center bg-navy-900 border border-gold-500/50 text-gold-400 font-bold px-3 py-1 rounded-full text-xs" data-id="element-253">
                      {pack.saves}
                    </span>}
                </div>}

              <p className={`text-sm leading-relaxed mt-3 ${selected ? 'text-blue-100/90' : 'text-blue-100/70'}`} data-id="element-254">
                {pack.description}
              </p>
            </div>

            {/* Right: price */}
            <div className="text-right shrink-0" data-id="element-255">
              <p className="text-gray-500 line-through text-sm sm:text-base" data-id="element-256">
                {pack.oldPrice}
              </p>
              <div className="flex items-baseline justify-end gap-1.5 mt-1" data-id="element-257">
                <span className={`font-bold leading-none ${isCompleta ? 'text-3xl sm:text-4xl text-gold-400' : 'text-2xl sm:text-3xl text-white'}`} data-id="element-258">
                  {pack.price}
                </span>
                <span className={`text-sm font-medium ${isCompleta ? 'text-gold-400/80' : 'text-blue-200'}`} data-id="element-259">
                  {pack.currency}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </button>;
}
export function PricingSection() {
  const [selected, setSelected] = useState<PackId>('completa');
  const activePack = PACKS[selected];
  return <section className="py-10 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto" data-id="element-260">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} className="text-left mb-8" data-id="element-261">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 leading-tight" data-id="element-262">
          Elegí tu{' '}
          <span className="text-gold-400" data-id="element-263">Guía Del Perro Tranquilo:</span>
        </h2>
        <p className="text-blue-200/80" data-id="element-264">
          Las dos opciones incluyen los 6 bonos estratégicos completos
        </p>
      </motion.div>

      <div className="space-y-4 mb-8" data-id="element-265">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} data-id="element-266">
          <PackCard pack={PACKS.esencial} selected={selected === 'esencial'} onSelect={() => setSelected('esencial')} data-id="element-267" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} data-id="element-268">
          <PackCard pack={PACKS.completa} selected={selected === 'completa'} onSelect={() => setSelected('completa')} data-id="element-269" />
        </motion.div>
      </div>

      {/* Single dynamic CTA */}
      <motion.a key={activePack.id} href={activePack.url} initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} className="block w-full py-4 px-6 bg-gradient-to-r from-gold-500 to-gold-400 hover:from-gold-400 hover:to-gold-300 text-navy-900 font-bold text-base sm:text-lg text-center rounded-2xl transition-all shadow-[0_0_30px_rgba(212,168,74,0.25)] hover:shadow-[0_0_40px_rgba(212,168,74,0.45)] transform hover:-translate-y-0.5" data-id="element-270">
        {activePack.ctaLabel}
      </motion.a>

      <div className="text-center mt-6 text-sm text-blue-200/60" data-id="element-271">
        💱 Pago en pesos argentinos · Mercado Pago | Tarjeta | Transferencia
      </div>
    </section>;
}